import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Collection, CollectionRef } from '../domain/collections';
import type { PersonRef } from '../domain/people';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import { fullName } from '../utils/people';
import { ActionError, ErrorState } from './PageStates';
import UserAvatar from './UserAvatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { BellOff, Lock } from 'lucide-react';

/**
 * Right after an accept that connected us (014 R15): the inviter's open
 * collections as I can see them now, each of which I may mute, and the
 * private ones I just joined. Nothing is muted unless I choose it.
 */
export default function PostAcceptStep({
  inviter,
  joined,
}: {
  inviter: PersonRef;
  joined: CollectionRef[];
}) {
  const navigate = useNavigate();
  const { collections, audience } = useRepositories();
  const open = useLoad<Collection[]>(
    () => collections.list('following').then((all) => all.filter((c) => c.owner.id === inviter.id)),
    inviter.id,
  );
  const [muted, setMuted] = useState<Set<string>>(new Set());
  const [muting, setMuting] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const mute = async (id: string) => {
    setMuting(id);
    setError(null);
    try {
      await audience.mute(id);
      setMuted((prev) => new Set(prev).add(id));
    } catch (e) {
      setError(e);
    } finally {
      setMuting(null);
    }
  };

  const listLabel = `${inviter.firstName}'s open collections`;

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <section className="space-y-8 rounded-3xl bg-card p-8">
        <div className="flex items-center gap-4">
          <UserAvatar person={inviter} className="size-16" textClassName="text-xl" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              You're connected with {inviter.firstName}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              You'll see new listings in {fullName(inviter)}'s open collections. Mute any you'd
              rather not hear about — you can change this later.
            </p>
          </div>
        </div>

        <div>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {listLabel}
          </h2>
          {open.status === 'loading' ? (
            <div role="status" aria-label="Loading collections" className="space-y-2">
              <Skeleton className="h-12 rounded-2xl" />
              <Skeleton className="h-12 rounded-2xl" />
            </div>
          ) : open.status === 'error' ? (
            <ErrorState message="Couldn't load the collections." onRetry={open.reload} />
          ) : open.data.length === 0 ? (
            <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground">
              {inviter.firstName} has no open collections you can see yet.
            </p>
          ) : (
            <ul aria-label={listLabel} className="space-y-2">
              {open.data.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-3 rounded-2xl bg-muted px-4 py-3"
                >
                  <span className="font-medium text-foreground">{c.name}</span>
                  {muted.has(c.id) ? (
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground">
                      <BellOff className="size-4" />
                      Muted
                    </span>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="rounded-full"
                      aria-label={`Mute ${c.name}`}
                      disabled={muting !== null}
                      onClick={() => mute(c.id)}
                    >
                      Mute
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {joined.length > 0 && (
          <div>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Collections you joined
            </h2>
            <ul aria-label="Collections you joined" className="space-y-2">
              {joined.map((c) => (
                <li key={c.id} className="flex items-center gap-3 rounded-2xl bg-muted px-4 py-3">
                  <Lock className="size-4 text-wood" />
                  <span className="font-medium text-foreground">{c.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error !== null && <ActionError error={error} />}

        <Button
          className="w-full rounded-full py-6 text-base font-bold"
          onClick={() => navigate('/my-collections')}
        >
          Done
        </Button>
      </section>
    </div>
  );
}
